import { ChangeEventHandler, FC, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import Scan from 'src/Modules/Scan';
import { ClearBtn, FileIcon } from 'src/Components';
import { Button, AlertMessage } from 'src/UI';
import { FieldGroup, IFormProps } from '..';
import { BaseViewModel, SimpleAttachmentViewModel } from '../../Models';
import st from './FileInput.module.scss';

interface ISimpleAttachmentControlProps extends IFormProps {
	model: SimpleAttachmentViewModel;
	allowScan?: boolean;
}

const SimpleAttachmentControl: FC<ISimpleAttachmentControlProps> = (props) => {
	const { model, readOnly = false, setFieldValue, allowScan = false } = props;
	const [inputProps, , helperProps] = useField(model.FullPath);
	const inputRef = useRef<HTMLInputElement>(null);

	const multiple = false;
	const accept = '';
	const isClear = true;

	useEffect(() => {
		if (typeof model.Value === 'object') {
			helperProps.setValue(model.Value, true);
		}
		// eslint-disable-next-line
	}, [model.Value]);

	/** Эмулирует клик по инпуту ( открывает окно выбора файла ) */
	const onChooseFile = () => {
		if (inputRef.current) {
			inputRef.current.click();
		}
	};

	/** Сохранение выбранных файлов ( метаданных ) */
	const onChangeValueHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
		const files =
			!!e.target?.files && e.target.files.length ? e.target.files : null;
		if (files) {
			onSetValue(files as FileList);
		}
	};

	/** Прикладываем результат сканирования */
	const setBlobFunctionHandler = async (response: any, count: number) => {
		const file = new File(
			[await response.result.arrayBuffer()],
			response.filename,
			{ type: response.result.type },
		);
		onSetValue([file] as unknown as FileList);
	};

	/** Удаление ранее выбранного файла */
	const onDeleteFile = (file: File, index: number) => {
		if (index !== -1) {
			const newFiles = [...inputProps.value];
			newFiles.splice(index, 1);
			setFieldValue(model.FullPath, newFiles as unknown as FileList);
		}
	};

	const onSetValue = (files: FileList | null) => {
		setFieldValue(model.FullPath, files);
		model.setValue(files);
	};

	/** Отображение файла */
	const renderFile = (file: any, index: number) => {
		const { type, name, size } = file;
		if (!name) {
			return null;
		}
		const mappedSize = BaseViewModel.attachmentsWithSizes?.[name];
		const displaySize =
			typeof mappedSize === 'number' && mappedSize > 0
				? mappedSize
				: size;
		return (
			<div key={'fileItem_' + index} className={st.file}>
				<div className="icon">
					<FileIcon file={file} />
				</div>
				<div className={st.info}>
					<h5 className="headline-small">{name}</h5>
					<span className="body-xsmall">
						{getMb(displaySize) + '     ' + type}
					</span>
				</div>
				{!readOnly && !!model.ControlEnabling && (
					<div className={st.tools}>
						<ClearBtn
							title="Удалить"
							onClick={() => {
								return onDeleteFile(file, index);
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	const classes = classNames(st.rdcControl, {
		disable: readOnly,
	});

	return (
		<FieldGroup
			hidden={!model.ControlVisibility}
			name={model.FullPath}
			className={classes}
			isInvalid={!model.IsValid && !!model.errorMessage}
			errorMsg={model.errorMessage}
			title={model.Caption ? model.Caption : 'Выбрать файл вложения'}
			width={model.Width ?? 12}
			isRequired={model.IsMandatory}
			readOnly={readOnly}
		>
			<input
				ref={inputRef}
				hidden
				multiple={multiple}
				accept={accept}
				type="file"
				onChange={onChangeValueHandler}
				disabled={readOnly}
			/>
			<div className={st.controls}>
				{(!inputProps.value?.length || multiple) &&
					!BaseViewModel.isReadOnly &&
					!readOnly && (
						<div className={st.toolbar}>
							<Button type="button" onClick={onChooseFile}>
								<svg
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="inherit"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M12 7.5L12 16.5M16.5 12H7.5"
										stroke="white"
										strokeWidth="2"
										strokeLinecap="round"
									/>
								</svg>
								Загрузить
							</Button>
							{!!allowScan && (
								<Scan
									enable={true}
									setBlobFunction={setBlobFunctionHandler}
								/>
							)}
							{(!!inputProps.value?.length ||
								!!model.getInitialOrCurrentFileName()) &&
								isClear && (
									<ClearBtn
										onClick={() => {
											return onSetValue(null);
										}}
									/>
								)}
						</div>
					)}
				{!model.IsValid && !!model.errorMessage && (
					<AlertMessage>{model.errorMessage}</AlertMessage>
				)}
			</div>
			{!!inputProps.value && !!inputProps.value.length && (
				<div className={st.files}>
					{Array.from(inputProps.value).map(renderFile)}
				</div>
			)}
		</FieldGroup>
	);
};

const getMb = (size: number) => {
	return ((size || 0) / 1024 / 1024).toFixed(1) + ' Мб';
};

export default SimpleAttachmentControl;
